import { CONFIG } from '../config.js';
import { APIService } from '../services/APIService.js';
import { ValidationService } from '../services/ValidationService.js';
import { DiscoveryState } from '../models/DiscoveryState.js';
import { Product } from '../models/Product.js';
import { Modal } from '../components/Modal.js';
import { Message } from '../components/Message.js';
import { KnowledgeReview } from '../components/KnowledgeReview.js';
import { DOMHelpers } from '../utils/DOMHelpers.js';
import { IntelligentInterview } from './IntelligentInterview.js';

/**
 * DiscoveryController
 * Main orchestration for campaign discovery flow
 *
 * Responsibilities:
 * - Initialize flow (edit/resume detection)
 * - Manage product selection (existing/custom/URL)
 * - Handle question sequence (8 questions)
 * - Process user input + validation
 * - Generate campaign
 */
export class DiscoveryController {
    constructor() {
        this.api = new APIService();
        this.validator = new ValidationService();
        this.state = new DiscoveryState();

        this.chatContainer = document.getElementById('chatContainer');
        this.inputArea = document.getElementById('inputArea');
        this.userInput = document.getElementById('userInput');
        this.sendBtn = document.getElementById('sendBtn');
        this.backBtn = document.getElementById('backBtn');

        this.questions = CONFIG.questions; // Fallback to static questions
        this.projectData = CONFIG.projects;
        this.intelligentInterview = null; // DV2-07: Intelligent interview instance

        this.typingIndicator = null;
    }

    /**
     * Initialize and check for resume/edit mode
     */
    async init() {
        // Note: Edit mode is handled by app.js loadEditMode() before DiscoveryController is created
        // This controller only handles new campaign creation flow

        // Check for saved session
        const hasSaved = this.state.restore();
        if (hasSaved) {
            this._showResumePrompt();
            return;
        }

        // Setup event listeners
        this._setupEventListeners();
    }

    /**
     * Setup event listeners for input and buttons
     */
    _setupEventListeners() {
        this.sendBtn.addEventListener('click', () => this.handleInput());
        this.userInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.handleInput();
            }
        });
        this.backBtn.addEventListener('click', () => this.goBack());
    }


    /**
     * Start discovery with a specific product
     */
    async startDiscovery(productId) {
        this.state.project = productId;
        this.state.projectTitle = this.projectData[productId]?.title || productId;

        // Remove welcome card
        const welcomeCard = document.getElementById('welcomeCard');
        if (welcomeCard) welcomeCard.remove();

        // Show input area
        this.inputArea.style.display = 'block';

        // Load product context
        await this._loadProductContext(productId);

        // Show knowledge review if available
        if (this.state.extractedKnowledge) {
            this._showKnowledgeReview();
        } else {
            await this._proceedToDiscovery();
        }
    }

    /**
     * Handle custom product creation
     */
    async startCustomProduct() {
        const result = await Modal.customProductModal(async (name) => {
            const productId = `custom-${Date.now()}`;
            const product = new Product({
                productId,
                name: name.trim(),
                source: 'manual-entry'
            });

            console.log('💾 Saving custom product:', { productId, name: name.trim() });

            try {
                // Save via API
                const response = await this.api.saveProduct(product.toJSON());
                console.log('✅ Product saved successfully:', response);

                // Verify productId was returned
                if (!response.productId) {
                    throw new Error('Server did not return productId in response');
                }

                // Add to projectData
                this.projectData[productId] = {
                    title: name.trim(),
                    description: 'Custom product entry'
                };

                return productId;
            } catch (error) {
                console.error('❌ Save product error:', error);
                throw new Error(`Failed to save product: ${error.message}`);
            }
        });

        if (result) {
            // Start discovery with this product
            await this.startDiscovery(result);
        }
    }

    /**
     * Handle URL scraping
     */
    async startURLScraper() {
        const result = await Modal.urlScraperModal(async (url) => {
            this._addMessage('ai', `Analyzing website: ${url}`);
            this._showTyping();

            try {
                const data = await this.api.scrapeURL(url);
                this._hideTyping();

                const confidencePercent = Math.round(data.avgConfidence * 100);
                this._addMessage('ai', `✓ Successfully extracted product information (${confidencePercent}% confidence)`);

                if (confidencePercent < 70) {
                    this._addMessage('ai', `⚠️ Confidence is below 70%. I recommend reviewing and editing the extracted data.`);
                }

                // Store extracted knowledge
                this.state.extractedKnowledge = data.preFilled;

                // Add to projectData
                this.projectData[data.productId] = {
                    title: data.preFilled.brandName?.value || 'Scraped Product',
                    description: 'Product data extracted from website'
                };

                return data.productId;
            } catch (error) {
                this._hideTyping();
                throw error;
            }
        });

        if (result) {
            await this.startDiscovery(result);
        }
    }

    /**
     * Ask next question in sequence
     */
    askQuestion() {
        if (this.state.currentQuestion >= this.questions.length) {
            this._showSummary();
            return;
        }

        const question = this.questions[this.state.currentQuestion];

        // Handle both 'text' (frontend) and 'question' (backend) field names
        const questionText = question.text || question.question || 'Question text missing';
        const text = questionText.replace('{project}', this.state.projectTitle);

        // Show current answer if in edit mode
        if (this.state.editMode && this.state.getAnswer(question.key)) {
            this._addMessage('ai', text);
            this._addMessage('ai', `📝 Current answer: "${this.state.getAnswer(question.key)}"`);
            this._addMessage('ai', `You can keep this answer or type a new one:`, question.quickReplies || []);
        } else {
            this._addMessage('ai', text, question.quickReplies || []);
        }

        this._updateBackButton();
    }

    /**
     * Handle user input
     */
    async handleInput(text = null) {
        const message = text || this.userInput.value.trim();
        if (!message) return;

        // Check for special commands from summary quick replies
        if (message === 'Generate Campaign') {
            this._addMessage('user', message);
            this.userInput.value = '';
            await this.generateCampaign();
            return;
        }

        if (message === 'Make Changes') {
            this._addMessage('user', message);
            this.userInput.value = '';
            this._addMessage('ai', 'No problem! Use the ← Back button to revisit previous questions.');
            return;
        }

        this._addMessage('user', message);
        this.userInput.value = '';

        // Store answer
        const currentQ = this.questions[this.state.currentQuestion];
        if (currentQ) {
            console.log(`💾 Saving answer for question ${this.state.currentQuestion}/${this.questions.length}:`, currentQ.key || currentQ.category, message);
            this.state.setAnswer(currentQ.key || currentQ.category, message);

            // Validate answer
            const context = {
                product: this.state.productContext,
                business: this.state.businessContext
            };
            const warnings = this.validator.validate(currentQ.key, message, context);

            if (warnings.length > 0) {
                this._showTyping();
                setTimeout(() => {
                    this._hideTyping();
                    warnings.forEach(w => {
                        this._addMessage('ai', `⚠️ **Validation**: ${w.message}`);
                    });
                }, 500);
            }
        }

        // Move to next question
        this.state.currentQuestion++;
        console.log(`⏭️ Moving to question ${this.state.currentQuestion}/${this.questions.length}`);
        this._showTyping();

        setTimeout(() => {
            this._hideTyping();
            this.askQuestion();
        }, 800);
    }

    /**
     * Go back one question
     */
    goBack() {
        if (this.state.currentQuestion === 0) return;

        // Remove last 2 messages (question + answer)
        this.state.removeLastMessages(2);

        // Remove from DOM
        const messages = this.chatContainer.querySelectorAll('.message');
        if (messages.length >= 2) {
            messages[messages.length - 1].remove();
            messages[messages.length - 2].remove();
        }

        // Go back one question
        this.state.currentQuestion--;

        // Clear answer
        const prevQuestion = this.questions[this.state.currentQuestion];
        if (prevQuestion) {
            this.state.clearAnswer(prevQuestion.key);
        }

        // Re-ask
        this.askQuestion();
    }

    /**
     * Show summary and generate campaign
     */
    async _showSummary() {
        this._addMessage('ai', '📋 **Discovery Complete!** Here\'s what we\'re going to create:');

        const summary = this._buildSummary();
        this._addMessage('ai', summary);

        this._addMessage('ai', '🚀 Ready to generate your campaign?', ['Generate Campaign', 'Make Changes']);
    }

    /**
     * Build summary text from answers
     */
    _buildSummary() {
        const answers = this.state.getAllAnswers();
        return Object.entries(answers)
            .filter(([key, value]) => value && key !== 'project')
            .map(([key, value]) => `• **${this._formatKey(key)}:** ${value}`)
            .join('\n');
    }

    /**
     * Format key for display (camelCase -> Title Case)
     */
    _formatKey(key) {
        return key.replace(/([A-Z])/g, ' $1').trim()
            .split(' ')
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
    }

    /**
     * Generate campaign
     */
    async generateCampaign() {
        this._addMessage('ai', '⏳ Generating your campaign...');
        this._showTyping();

        try {
            const result = await this.api.generateCampaign({
                ...this.state.getAllAnswers(),
                project: this.state.project,
                projectTitle: this.state.projectTitle
            });

            this._hideTyping();

            if (result.success) {
                this._addMessage('ai', `✅ **Campaign Generated!**`);
                this._addMessage('ai', `Campaign ID: ${result.campaignId}`);

                // Auto-open dashboard in new tab
                const dashboardUrl = result.dashboardUrl || `/tools/dashboard/web/campaign-dashboard.html?campaignId=${result.campaignId}`;
                this._addMessage('ai', `Opening campaign dashboard in new tab...`);

                setTimeout(() => {
                    window.open(dashboardUrl, '_blank');
                }, 1000);

                // Clear saved session
                this.state.clear();
            } else {
                throw new Error(result.message || 'Generation failed');
            }
        } catch (error) {
            this._hideTyping();
            this._addMessage('ai', `❌ Error: ${error.message}`);
        }
    }

    /**
     * UI Helpers
     */
    _addMessage(sender, text, quickReplies = []) {
        const msg = new Message(sender, text, {
            quickReplies,
            onQuickReply: (reply) => this.handleInput(reply)
        });
        msg.mount(this.chatContainer);
        this.state.addMessage(sender, text);
        DOMHelpers.scrollToBottom(this.chatContainer);
    }

    _showTyping() {
        this.typingIndicator = document.getElementById('typingIndicator');
        if (this.typingIndicator) {
            this.typingIndicator.style.display = 'flex';
            DOMHelpers.scrollToBottom(this.chatContainer);
        }
    }

    _hideTyping() {
        if (this.typingIndicator) {
            this.typingIndicator.style.display = 'none';
        }
    }

    _updateBackButton() {
        this.backBtn.disabled = this.state.currentQuestion === 0;
    }

    /**
     * Load product and business context from API
     */
    async _loadProductContext(productId) {
        try {
            const [product, business] = await Promise.all([
                this.api.getProduct(productId).catch(() => null),
                this.api.getBusiness(productId).catch(() => null)
            ]);

            this.state.setProductContext(product);
            this.state.setBusinessContext(business);

            // Store as extracted knowledge if available
            if (product || business) {
                this.state.extractedKnowledge = {
                    product,
                    business
                };
            }
        } catch (error) {
            console.error('Failed to load product context:', error);
        }
    }

    /**
     * Show knowledge review component
     */
    _showKnowledgeReview() {
        const review = new KnowledgeReview(this.state.extractedKnowledge, {
            onConfirm: () => this._proceedToDiscovery(),
            onSkip: () => {
                this.state.extractedKnowledge = null;
                this._proceedToDiscovery();
            },
            onEdit: async (updated) => {
                this.state.extractedKnowledge = updated;

                // Persist edited knowledge to product file
                if (this.state.productContext && this.state.project) {
                    try {
                        const updatedProduct = {
                            ...this.state.productContext,
                            description: updated.product?.description || this.state.productContext.description || '',
                            features: updated.product?.features || this.state.productContext.features || [],
                            targetAudience: updated.product?.targetAudience || this.state.productContext.targetAudience || '',
                            offer: updated.product?.offer || this.state.productContext.offer || {}
                        };

                        await this.api.saveProduct(updatedProduct);
                        console.log('✅ Product knowledge saved to disk:', this.state.project);

                        // Update productContext with edited data
                        this.state.setProductContext(updatedProduct);
                    } catch (error) {
                        console.error('❌ Failed to save product knowledge:', error);
                        this._addMessage('ai', `⚠️ Warning: Changes saved in session but couldn't persist to disk. They'll be used for this campaign.`);
                    }
                }
            }
        });
        review.mount(this.chatContainer);
        DOMHelpers.scrollToBottom(this.chatContainer);
    }

    /**
     * Proceed to discovery questions
     * DV2-07: Initialize intelligent interview with product context
     */
    async _proceedToDiscovery() {
        this._addMessage('ai', `Perfect! Let's create a high-converting campaign for ${this.state.projectTitle}.`);

        // Initialize intelligent interview if product context is available
        if (this.state.productContext) {
            try {
                this.intelligentInterview = new IntelligentInterview();
                const questions = await this.intelligentInterview.init(this.state.productContext);

                // Replace static questions with intelligent questions
                this.questions = questions;

                console.log(`✨ DV2-07: Generated ${questions.length} contextual questions (was ${CONFIG.questions.length} static)`);
            } catch (error) {
                console.error('Intelligent Interview initialization failed, falling back to static questions:', error);
                // Keep CONFIG.questions as fallback
            }
        }

        setTimeout(() => this.askQuestion(), 1000);
    }

    /**
     * Show resume prompt for saved session
     */
    _showResumePrompt() {
        // Create button elements using DOMHelpers
        const resumeBtn = DOMHelpers.createElement('button', {
            className: 'btn btn-primary',
            onClick: () => {
                modal.close();
                this._resumeSession();
            }
        }, ['Resume']);

        const freshBtn = DOMHelpers.createElement('button', {
            className: 'btn btn-secondary',
            onClick: () => {
                this.state.clear();
                modal.close();
                this._setupEventListeners();
            }
        }, ['Start Fresh']);

        const modal = new Modal({
            title: 'Resume Discovery',
            content: `
                <p>You have a saved discovery session for <strong>${this.state.projectTitle}</strong>.</p>
                <p>Would you like to continue where you left off?</p>
            `,
            footer: [resumeBtn, freshBtn]
        });

        modal.mount(document.body);
    }

    /**
     * Resume saved session
     */
    async _resumeSession() {
        // Remove welcome card
        const welcomeCard = document.getElementById('welcomeCard');
        if (welcomeCard) welcomeCard.remove();

        // Show input area
        this.inputArea.style.display = 'block';

        // Setup event listeners
        this._setupEventListeners();

        // Restore messages
        const messages = this.state.conversation || [];
        messages.forEach(msg => {
            const message = new Message(msg.sender, msg.text, {});
            message.mount(this.chatContainer);
        });

        // Load product context and regenerate intelligent questions
        await this._loadProductContext(this.state.project);

        // Initialize intelligent interview to regenerate questions
        if (this.state.productContext) {
            try {
                this.intelligentInterview = new IntelligentInterview();
                const questions = await this.intelligentInterview.init(this.state.productContext);
                this.questions = questions;
                console.log(`✨ Resume: Regenerated ${questions.length} intelligent questions`);
            } catch (error) {
                console.error('Failed to regenerate intelligent questions on resume, using static fallback:', error);
                // Keep CONFIG.questions as fallback
            }
        }

        // Check if we need to re-ask the current question or just wait for user input
        const lastMessage = messages[messages.length - 1];
        const isWaitingForAnswer = lastMessage && lastMessage.sender === 'ai';

        if (isWaitingForAnswer) {
            // Last message was AI asking a question - just enable input, don't re-ask
            this._addMessage('ai', '👋 Welcome back! Please continue with your answer.');
            this._updateBackButton();
        } else {
            // Last message was user answering - ask next question
            this._addMessage('ai', '👋 Welcome back! Let\'s continue...');
            setTimeout(() => this.askQuestion(), 1000);
        }
    }
}
